/** Browser-only PDF presentation for canonical report documents. */

import type { DownloadableReport, ReportBlockEmphasis, ReportTextItem } from "./reportDocument";

type JsPdfDoc = import("jspdf").jsPDF;

// Letter pages in points. All content is drawn with native jsPDF text (never
// canvas or an image), so the visual hierarchy does not compromise search,
// copy/paste, or downstream PDF text extraction.
const PAGE_MARGIN_PT = 48;
const PAGE_BOTTOM_PT = 716;
const CONTENT_WIDTH_PT = 612 - PAGE_MARGIN_PT * 2;
const BODY_FONT_PT = 9.5;
const BODY_LINE_PT = 13.5;

const COLOR = {
  ink: [22, 22, 26] as const,
  muted: [82, 82, 90] as const,
  faint: [120, 120, 130] as const,
  border: [220, 223, 231] as const,
  surface: [248, 249, 252] as const,
  signal: [67, 97, 238] as const,
  positiveText: [43, 63, 152] as const,
  signalTint: [242, 245, 255] as const,
  draftText: [137, 84, 13] as const,
  draftTint: [255, 247, 230] as const,
  finalText: [24, 105, 65] as const,
  finalTint: [235, 248, 240] as const,
};

type PdfColor = readonly [number, number, number];

interface ReportRenderState {
  doc: JsPdfDoc;
  report: DownloadableReport;
  y: number;
  activeSection: string | null;
}

interface ReportPageRange {
  report: DownloadableReport;
  firstPage: number;
  lastPage: number;
}

function setTextColor(doc: JsPdfDoc, color: PdfColor): void {
  doc.setTextColor(color[0], color[1], color[2]);
}

function setFillColor(doc: JsPdfDoc, color: PdfColor): void {
  doc.setFillColor(color[0], color[1], color[2]);
}

function setDrawColor(doc: JsPdfDoc, color: PdfColor): void {
  doc.setDrawColor(color[0], color[1], color[2]);
}

function reportStatus(report: DownloadableReport): string {
  return report.signed ? "FINAL REPORT" : "DRAFT / NOT SIGNED";
}

function reportCaseIdentifier(report: DownloadableReport): string {
  return report.caseIdentifier?.trim() || report.filename.replace(/\.pdf$/i, "");
}

function drawBrand(doc: JsPdfDoc, y: number): void {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  setTextColor(doc, COLOR.signal);
  doc.text("a2z", PAGE_MARGIN_PT, y);

  doc.setFontSize(7.5);
  setTextColor(doc, COLOR.muted);
  doc.text("RADIOLOGY AI", PAGE_MARGIN_PT + 27, y - 0.5);
}

function drawStatusPill(doc: JsPdfDoc, report: DownloadableReport, y: number): void {
  const label = reportStatus(report);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  const width = doc.getTextWidth(label) + 18;
  const x = 612 - PAGE_MARGIN_PT - width;
  setFillColor(doc, report.signed ? COLOR.finalTint : COLOR.draftTint);
  doc.roundedRect(x, y - 10, width, 18, 9, 9, "F");
  setTextColor(doc, report.signed ? COLOR.finalText : COLOR.draftText);
  doc.text(label, x + width / 2, y + 2, { align: "center" });
}

/** First-page document identity and metadata. Values stay in a simple
 * top-to-bottom reading order so pdftotext and screen readers see the same
 * logical sequence as a person. */
function drawFirstPageHeader(doc: JsPdfDoc, report: DownloadableReport): number {
  setFillColor(doc, COLOR.signal);
  doc.rect(0, 0, 612, 5, "F");
  drawBrand(doc, 35);
  drawStatusPill(doc, report, 33);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  setTextColor(doc, COLOR.ink);
  doc.text("Imaging report", PAGE_MARGIN_PT, 84);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  setTextColor(doc, COLOR.muted);
  doc.text(report.signed ? "FINAL REPORT" : "AI-GENERATED DRAFT REPORT", PAGE_MARGIN_PT, 101);

  const caseId = reportCaseIdentifier(report);
  const showAccession = report.accessionNumber !== undefined;
  const accession = report.accessionNumber ?? "Not available";
  const caseWidth = showAccession ? 322 : CONTENT_WIDTH_PT;
  const caseLines = doc.splitTextToSize(caseId, caseWidth - 28) as string[];
  const accessionLines = !showAccession
    ? []
    : (doc.splitTextToSize(accession, CONTENT_WIDTH_PT - caseWidth - 28) as string[]);
  const metadataHeight = Math.max(
    62,
    45 + (Math.max(caseLines.length, accessionLines.length) - 1) * 11,
  );
  const metadataTop = 120;

  setFillColor(doc, COLOR.surface);
  setDrawColor(doc, COLOR.border);
  doc.roundedRect(PAGE_MARGIN_PT, metadataTop, CONTENT_WIDTH_PT, metadataHeight, 4, 4, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  setTextColor(doc, COLOR.faint);
  doc.text("CASE IDENTIFIER", PAGE_MARGIN_PT + 14, metadataTop + 19);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  setTextColor(doc, COLOR.ink);
  doc.text(caseLines, PAGE_MARGIN_PT + 14, metadataTop + 37, { lineHeightFactor: 1.15 });

  if (showAccession) {
    const accessionX = PAGE_MARGIN_PT + caseWidth;
    setDrawColor(doc, COLOR.border);
    doc.line(accessionX, metadataTop + 12, accessionX, metadataTop + metadataHeight - 12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    setTextColor(doc, COLOR.faint);
    doc.text("ACCESSION", accessionX + 14, metadataTop + 19);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    setTextColor(doc, COLOR.ink);
    doc.text(accessionLines, accessionX + 14, metadataTop + 37, { lineHeightFactor: 1.15 });
  }

  return metadataTop + metadataHeight + 30;
}

function drawContinuationHeader(doc: JsPdfDoc, report: DownloadableReport): number {
  setFillColor(doc, COLOR.signal);
  doc.rect(0, 0, 612, 4, "F");
  drawBrand(doc, 32);
  drawStatusPill(doc, report, 30);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  setTextColor(doc, COLOR.ink);
  doc.text("IMAGING REPORT / CONTINUED", PAGE_MARGIN_PT, 61);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  setTextColor(doc, COLOR.muted);
  const identity = [`Case: ${reportCaseIdentifier(report)}`];
  if (report.accessionNumber !== undefined) {
    identity.push(`Accession: ${report.accessionNumber ?? "Not available"}`);
  }
  const identityLines = doc.splitTextToSize(identity.join("  /  "), CONTENT_WIDTH_PT) as string[];
  doc.text(identityLines, PAGE_MARGIN_PT, 77, { lineHeightFactor: 1.2 });
  const dividerY = 82 + Math.max(0, identityLines.length - 1) * 9;
  setDrawColor(doc, COLOR.border);
  doc.line(PAGE_MARGIN_PT, dividerY, 612 - PAGE_MARGIN_PT, dividerY);
  return dividerY + 22;
}

function drawSectionHeading(doc: JsPdfDoc, title: string, y: number): number {
  setFillColor(doc, COLOR.signal);
  doc.roundedRect(PAGE_MARGIN_PT, y - 10, 3, 17, 1.5, 1.5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setTextColor(doc, COLOR.ink);
  doc.text(`${title}:`, PAGE_MARGIN_PT + 13, y + 2);
  setDrawColor(doc, COLOR.border);
  doc.line(PAGE_MARGIN_PT + 13, y + 11, 612 - PAGE_MARGIN_PT, y + 11);
  return y + 31;
}

function continueReport(state: ReportRenderState): void {
  state.doc.addPage();
  state.y = drawContinuationHeader(state.doc, state.report);
  if (state.activeSection) state.y = drawSectionHeading(state.doc, state.activeSection, state.y);
}

function ensureSpace(state: ReportRenderState, height: number): void {
  if (state.y + height > PAGE_BOTTOM_PT) continueReport(state);
}

function setBlockTextStyle(doc: JsPdfDoc, emphasis: ReportBlockEmphasis): void {
  doc.setFont("helvetica", emphasis === "ai-positive" ? "bold" : "normal");
  doc.setFontSize(BODY_FONT_PT);
  setTextColor(doc, emphasis === "ai-positive" ? COLOR.positiveText : COLOR.ink);
}

function drawWrappedLines(
  state: ReportRenderState,
  lines: string[],
  x: number,
  width: number,
  emphasis: ReportBlockEmphasis,
  lineHeight = BODY_LINE_PT,
): void {
  for (const sourceLine of lines) {
    const wrapped = state.doc.splitTextToSize(sourceLine, width) as string[];
    for (const line of wrapped) {
      if (state.y + lineHeight > PAGE_BOTTOM_PT) {
        continueReport(state);
        setBlockTextStyle(state.doc, emphasis);
      }
      state.doc.text(line, x, state.y);
      state.y += lineHeight;
    }
  }
}

function drawLabeledBlock(state: ReportRenderState, label: string, items: ReportTextItem[]): void {
  const itemWidth = CONTENT_WIDTH_PT - (items.length > 1 ? 28 : 12);
  const firstItemLines = state.doc.splitTextToSize(items[0]?.text ?? "", itemWidth) as string[];
  ensureSpace(state, 14 + Math.min(firstItemLines.length, 2) * BODY_LINE_PT);
  state.doc.setFont("helvetica", "bold");
  state.doc.setFontSize(7.5);
  setTextColor(state.doc, COLOR.signal);
  state.doc.text(`${label.toUpperCase()}:`, PAGE_MARGIN_PT + 6, state.y);
  state.y += 14;

  for (const item of items) {
    ensureSpace(state, BODY_LINE_PT + 3);
    const itemX = PAGE_MARGIN_PT + (items.length > 1 ? 19 : 6);
    if (items.length > 1) {
      setFillColor(state.doc, item.emphasis === "ai-positive" ? COLOR.positiveText : COLOR.muted);
      state.doc.circle(PAGE_MARGIN_PT + 9, state.y - 3.2, 1.7, "F");
    }
    setBlockTextStyle(state.doc, item.emphasis);
    drawWrappedLines(state, [item.text], itemX, itemWidth, item.emphasis);
    if (items.length > 1) state.y += 3;
  }
  state.y += 7;
}

function drawNumberedBlock(
  state: ReportRenderState,
  number: string,
  body: string,
  emphasis: ReportBlockEmphasis,
): void {
  const textWidth = CONTENT_WIDTH_PT - 50;
  const remaining = state.doc.splitTextToSize(body, textWidth) as string[];
  let firstChunk = true;

  while (remaining.length) {
    ensureSpace(state, 32);
    const capacity = Math.max(1, Math.floor((PAGE_BOTTOM_PT - state.y - 8) / BODY_LINE_PT));
    const chunk = remaining.splice(0, capacity);
    const blockHeight = Math.max(31, chunk.length * BODY_LINE_PT + 18);
    const blockTop = state.y - 11;

    setFillColor(state.doc, COLOR.signalTint);
    state.doc.roundedRect(PAGE_MARGIN_PT, blockTop, CONTENT_WIDTH_PT, blockHeight, 4, 4, "F");
    if (firstChunk) {
      state.doc.setFont("helvetica", "bold");
      state.doc.setFontSize(9);
      setTextColor(state.doc, COLOR.signal);
      state.doc.text(`${number}.`, PAGE_MARGIN_PT + 15, state.y + 1);
    }
    setBlockTextStyle(state.doc, emphasis);
    for (const line of chunk) {
      state.doc.text(line, PAGE_MARGIN_PT + 39, state.y);
      state.y += BODY_LINE_PT;
    }
    state.y += 8;
    firstChunk = false;
    if (remaining.length) continueReport(state);
  }
}

function drawParagraph(
  state: ReportRenderState,
  text: string,
  emphasis: ReportBlockEmphasis,
): void {
  const lines = state.doc.splitTextToSize(text, CONTENT_WIDTH_PT - 12) as string[];
  ensureSpace(state, Math.min(lines.length, 2) * BODY_LINE_PT);
  setBlockTextStyle(state.doc, emphasis);
  drawWrappedLines(state, [text], PAGE_MARGIN_PT + 6, CONTENT_WIDTH_PT - 12, emphasis);
  state.y += 8;
}

function drawDisclosure(state: ReportRenderState, text: string): void {
  const lines = state.doc.splitTextToSize(text, CONTENT_WIDTH_PT - 12) as string[];
  ensureSpace(state, Math.min(lines.length, 2) * 11 + 4);
  state.doc.setFont("helvetica", "italic");
  state.doc.setFontSize(7.5);
  setTextColor(state.doc, COLOR.muted);
  state.doc.text(lines, PAGE_MARGIN_PT + 6, state.y, { lineHeightFactor: 1.3 });
  state.y += lines.length * 10 + 8;
}

/** Full-size, draft-amber statement — a notice reports missing content
 * (e.g. no Impression could be generated), so it must read as a warning,
 * never as fine print. */
function drawNotice(state: ReportRenderState, text: string): void {
  const lines = state.doc.splitTextToSize(text, CONTENT_WIDTH_PT - 12) as string[];
  ensureSpace(state, Math.min(lines.length, 2) * BODY_LINE_PT + 4);
  state.doc.setFont("helvetica", "bold");
  state.doc.setFontSize(BODY_FONT_PT);
  setTextColor(state.doc, COLOR.draftText);
  state.doc.text(lines, PAGE_MARGIN_PT + 6, state.y, { lineHeightFactor: 1.4 });
  state.y += lines.length * BODY_LINE_PT + 8;
}

function addReportPages(
  doc: JsPdfDoc,
  report: DownloadableReport,
  first: boolean,
): ReportPageRange {
  if (!first) doc.addPage();
  const firstPage = doc.getNumberOfPages();
  const state: ReportRenderState = {
    doc,
    report,
    y: drawFirstPageHeader(doc, report),
    activeSection: null,
  };

  for (const section of report.document.sections) {
    state.activeSection = section.title;
    ensureSpace(state, 44);
    state.y = drawSectionHeading(doc, section.title, state.y);

    for (const block of section.blocks) {
      if (block.kind === "numbered") {
        drawNumberedBlock(state, block.number, block.text, block.emphasis);
      } else if (block.kind === "labeled") {
        drawLabeledBlock(state, block.label, block.items);
      } else if (block.kind === "disclosure") {
        drawDisclosure(state, block.text);
      } else if (block.kind === "notice") {
        drawNotice(state, block.text);
      } else {
        drawParagraph(state, block.text, block.emphasis);
      }
    }
    state.y += 12;
  }

  return { report, firstPage, lastPage: doc.getNumberOfPages() };
}

function drawPageFooters(doc: JsPdfDoc, ranges: ReportPageRange[]): void {
  for (const range of ranges) {
    const total = range.lastPage - range.firstPage + 1;
    for (let page = range.firstPage; page <= range.lastPage; page += 1) {
      doc.setPage(page);
      setDrawColor(doc, COLOR.border);
      doc.line(PAGE_MARGIN_PT, 744, 612 - PAGE_MARGIN_PT, 744);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      setTextColor(doc, COLOR.faint);
      doc.text(range.report.filename, PAGE_MARGIN_PT, 762);
      doc.text(`Page ${page - range.firstPage + 1} of ${total}`, 612 - PAGE_MARGIN_PT, 762, {
        align: "right",
      });
    }
  }
}

/** Build one PDF with each report starting on its own page. jspdf is loaded
 * on demand so the viewer bundle doesn't carry it. Exported for tests. */
export async function buildReportsPdf(reports: DownloadableReport[]): Promise<JsPdfDoc> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  doc.setProperties({
    title: reports.length === 1 ? reports[0].filename : "Imaging report export",
    subject: "Imaging reports",
    creator: "a2z Radiology AI",
  });
  const ranges = reports.map((report, index) => addReportPages(doc, report, index === 0));
  drawPageFooters(doc, ranges);
  return doc;
}

/** Exports all selected case reports as one portable PDF document. */
export async function downloadCombinedReports(
  reports: DownloadableReport[],
  filename: string,
): Promise<void> {
  const doc = await buildReportsPdf(reports);
  doc.save(filename);
}

/** One shared statement of what a bulk export could not deliver, shown after
 * the PDF downloads. Null when every requested report exported complete. */
export function summarizeExportShortfall(
  requested: number,
  exported: DownloadableReport[],
): string | null {
  const unavailable = exported.filter((report) => report.impressionUnavailable).length;
  const skipped = requested - exported.length;
  if (!unavailable && !skipped) return null;
  const complete = exported.length - unavailable;
  const parts: string[] = [];
  if (unavailable) {
    parts.push(
      `${unavailable} report${unavailable === 1 ? " says" : "s say"} in the PDF that no Impression could be generated`,
    );
  }
  if (skipped) {
    parts.push(
      `${skipped} case${skipped === 1 ? " has" : "s have"} no report data yet and ${skipped === 1 ? "was" : "were"} skipped`,
    );
  }
  return `Exported ${exported.length} of ${requested} reports, ${complete} complete: ${parts.join("; ")}.`;
}

export async function downloadReport(report: DownloadableReport): Promise<void> {
  const doc = await buildReportsPdf([report]);
  doc.save(report.filename);
}
